import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CaretRight } from "@phosphor-icons/react";
import { NAV_FLOWS_BUILT } from "./DashboardLayout";
import { HUB_META } from "../lib/legacyRoutes";
import { PageHeader } from "../components/ui/kaizen";

const clean = (l) => l.replace(/^[^\p{L}\p{N}]+/u, "").trim() || l;
const fold = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Plan du site : 5 flux → pages → onglets des hubs. Point de repère unique pour « où est quoi ».
export default function AllPagesModule() {
  const [q, setQ] = useState("");
  const fq = fold(q.trim());
  const flows = useMemo(() => NAV_FLOWS_BUILT.map((f) => ({
    ...f,
    items: f.items.map((it) => ({ ...it, tabs: HUB_META[it.to]?.tabs || [] }))
      .filter((it) => !fq || fold(it.label).includes(fq) || fold(it.desc).includes(fq) || it.tabs.some((t) => fold(t.label).includes(fq))),
  })).filter((f) => f.items.length), [fq]);
  const total = NAV_FLOWS_BUILT.reduce((n, f) => n + f.items.length, 0);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="all-pages">
      <PageHeader title="Plan du site" subtitle={`${total} pages réparties en 5 flux · les pages à onglets regroupent les anciens modules`} />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une page ou un onglet…" data-testid="all-pages-search"
        className="bg-zinc-950 border border-zinc-700 focus:border-amber-500/60 outline-none rounded-lg px-4 py-2.5 text-sm text-zinc-100 w-full max-w-md mb-8" />
      <div className="grid gap-8 lg:grid-cols-2">
        {flows.map((f) => {
          const FIcon = f.icon;
          return (
            <section key={f.id} data-testid={`sitemap-flow-${f.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6">
              <header className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center"><FIcon size={20} weight="duotone" className="text-amber-400" /></span>
                <div><h2 className="text-lg font-bold text-zinc-50 leading-tight">{f.label}</h2><p className="text-xs text-zinc-500">{f.desc}</p></div>
                <span className="ml-auto text-xs text-zinc-500 tabular-nums">{f.items.length}</span>
              </header>
              <ul className="space-y-1.5">
                {f.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <li key={it.to}>
                      <Link to={it.to} data-testid={`allpage-${it.to.replace(/\//g, "-").slice(1)}`}
                        className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-zinc-800/70 transition-colors">
                        <Icon size={16} weight="duotone" className="text-amber-500 shrink-0" />
                        <span className="text-sm text-zinc-100 font-medium">{clean(it.label)}</span>
                        {it.desc && <span className="text-xs text-zinc-500 truncate hidden sm:inline">· {it.desc}</span>}
                        <CaretRight size={12} className="ml-auto text-zinc-700 group-hover:text-amber-400 shrink-0" />
                      </Link>
                      {it.tabs.length > 0 && (
                        <div className="ml-10 mb-1 flex flex-wrap gap-1.5">
                          {it.tabs.map((t) => (
                            <Link key={t.key} to={`${it.to}?tab=${t.key}`} data-testid={`allpage-tab-${it.to.replace("/app/", "")}-${t.key}`}
                              className="text-[11px] px-2 py-0.5 rounded-full border border-zinc-800 text-zinc-400 hover:border-amber-500/50 hover:text-amber-300 transition-colors">{t.label}</Link>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
